// cadFieldMap.ts — Positional column maps for cv-cad XRecord → cv-web field translation
//
// cv-cad stores panel data as flat positional values: prefix_row_col
// cv-web uses named semantic fields: mpvar.thickness, mpvar[n].data.width, ...
//
// This file defines the mapping table between the two. Each section has a map of
// column-index → { field, type }. Unmapped columns produce import warnings.
//
// COLUMN MAPPING BASIS:
//   Source-mode columns are derived from convert.lsp variable ordering in
//   src/x32/TB11-01x32/convert.lsp. For array sections, columns are
//   per-slot (e.g., rovar slot has 9 fields → cols 1-9 per row).
//
//   VLX-mode (PB11/VM102) uses DIFFERENT column counts and ordering.
//   The VLX was compiled from source that doesn't exist in this repo.
//   VLX empirical mappings are noted where confirmed from sample data
//   (reports/vm102-exports/XP-CV-V11-202632--named.json).
//
// PREFIX RESOLUTION:
//   VLX exports use 2-letter prefixes (mp_1_1, so_1_1, dr_1_1).
//   Source exports use *var prefixes (mpvar_1_1, wdvar_1_1, drvar_1_1).
//   Both are resolved to these semantic keys by PREFIX_TO_SECTION in the adapter.

// ── Types ──────────────────────────────────────────────────────────────────────

export type FieldType = 'number' | 'string' | 'boolean';

export interface FieldMapping {
  field: string;
  type:  FieldType;
}

/** Maps 1-based column index to a named field + type. */
export type ColumnMap = Record<number, FieldMapping>;

/** Maps semantic section name to its column map and metadata. */
export type SectionFieldMap = Record<string, {
  varKey:    string;       // cv-web *var key (e.g. "mpvar")
  kind:      'object' | 'array';  // mpvar/tpvar/lbvar/chvar are objects; rest are slot arrays
  columns:   ColumnMap;
  maxSlots?: number;       // For arrays: cv-web default slot count (warn if exceeded)
}>;

// ── Prefix resolution (VLX 2-letter + source-mode *var → semantic key) ─────

export const PREFIX_TO_SECTION: Record<string, string> = {
  // VLX 2-letter prefixes (PB11/VLX-created drawings)
  mp: 'mainPanel',
  so: 'standardOpenings',
  bo: 'blockoutOpenings',
  dr: 'manDoors',
  dl: 'dockLevelers',
  pl: 'pilasters',
  lt: 'lintels',
  ch: 'chamfers',
  fs: 'footingSteps',
  sd: 'slabDowels',
  wc: 'weldConnections',
  pp: 'pickPoints',
  bp: 'bracePoints',
  ro: 'blockoutOpenings',
  fh: 'horizontalFeatureStrips',
  fv: 'verticalFeatureStrips',
  lb: 'ledgerBars',
  tp: 'topPlates',
  ts: 'topSteps',
  ss: 'spandrelSeats',
  sb: 'squareBlockouts',
  rb: 'roundBlockouts',
  rv: 'revisions',
  wd: 'standardOpenings',
  // Source-mode *var prefixes (TB11 source-mode drawings)
  mpvar: 'mainPanel',
  rovar: 'blockoutOpenings',
  wdvar: 'standardOpenings',
  drvar: 'manDoors',
  dlvar: 'dockLevelers',
  plvar: 'pilasters',
  llvar: 'lintels',
  lbvar: 'ledgerBars',
  tpvar: 'topPlates',
  fhvar: 'horizontalFeatureStrips',
  fvvar: 'verticalFeatureStrips',
  chvar: 'chamfers',
  tsvar: 'topSteps',
  fsvar: 'footingSteps',
  ssvar: 'spandrelSeats',
  sbvar: 'squareBlockouts',
  rbvar: 'roundBlockouts',
  sdvar: 'slabDowels',
  bpvar: 'bracePoints',
  ppvar: 'pickPoints',
  wcvar: 'weldConnections',
};

/** Resolve a raw export prefix to a CAD_FIELD_MAP semantic key. */
export function resolvePrefix(rawPrefix: string): string {
  return PREFIX_TO_SECTION[rawPrefix] ?? rawPrefix;
}

// ── Default slot counts in cv-web (from panelvar.ts defaultPanelData) ──────

export const CV_WEB_SLOT_COUNTS: Record<string, number> = {
  rovar: 4,
  wdvar: 6,
  drvar: 6,
  dlvar: 3,
  sbvar: 6,
  rbvar: 6,
  fhvar: 19,
  fvvar: 19,
  plvar: 2,
  llvar: 2,
  tsvar: 2,
  fsvar: 2,
  ssvar: 2,
  sdvar: 4,
  bpvar: 8,
  ppvar: 8,
  wcvar: 15,
};

// ── The mapping table ──────────────────────────────────────────────────────────
//
// SOURCE-MODE column indices: derived from convert.lsp variable list ordering.
// These are correct when importing TB11 source-mode exports (*var prefixes).
//
// VLX-MODE WARNING: VLX exports (2-letter prefixes) have different column
// counts and ordering. The VLX was compiled from source not in this repo.
// Mappings verified against VM102 VLX export are marked [VLX-CONFIRMED].
// All others are [SOURCE-ONLY] — correct for TB11, unverified for VLX.
//
// convert.lsp var lists define positional ordering per section.
// For object sections: column = position in flat var list.
// For array sections: column = position within one slot's field group.

export const CAD_FIELD_MAP: SectionFieldMap = {

  // ── mainPanel (mpvar) — 38 source-mode columns ───────────────────────────
  // Source-mode: mpvar has 18 base vars + 20 section-enable toggles.
  // VLX-mode: mp has 25 columns with DIFFERENT ordering.
  //
  // convert.lsp mpvar ordering:
  //   1:mpv# 2:mppn 3:mpco 4:mplo 5:mpdb 6:mpp# 7:mpsc 8:mpvw
  //   9:mpx1 10:mpe1 11:mpm1 12:mpm2 13:mpw1 14:mpl1 15:mpr1
  //   16:mpp1 17:mpd1 18:mpa1 19-38:section toggles
  //
  // VLX confirmed (from VM102 AAA panel export):
  //   mp_1_2="DAT"→drawBy, mp_1_5=384→width, mp_1_22=7.25→thickness
  //   mp_1_8=-35→bottomElev
  mainPanel: {
    varKey:  'mpvar',
    kind:    'object',
    columns: {
      // Source-mode positions (convert.lsp mpvar ordering)
      1:  { field: 'revision',    type: 'string' },   // mpv#
      2:  { field: 'projectName', type: 'string' },   // mppn
      3:  { field: 'color',       type: 'string' },   // mpco
      4:  { field: 'notes',       type: 'string' },   // mplo (location)
      5:  { field: 'drawBy',      type: 'string' },   // mpdb
      6:  { field: 'panelMark',   type: 'string' },   // mpp#
      // 7: mpsc — scale (no cv-web field)
      // 8: mpvw — view mode (no cv-web field)
      9:  { field: 'thickness',   type: 'number' },   // mpx1
      10: { field: 'bottomElev',  type: 'number' },   // mpe1
      // 11: mpm1 — angle/slope left (no direct cv-web field)
      // 12: mpm2 — angle/slope right (no direct cv-web field)
      13: { field: 'width',       type: 'number' },   // mpw1
      14: { field: 'height',      type: 'number' },   // mpl1
      15: { field: 'roofLine',    type: 'number' },   // mpr1 (tentative)
      // 16: mpp1 — peak height? (uncertain)
      17: { field: 'date',        type: 'string' },   // mpd1
      // 18: mpa1 — angle? (uncertain)
      // 19-38: section-enable toggles (not in cv-web MpVar)
    },
  },

  // ── standardOpenings (wdvar) — 11 cols/slot, up to 12 slots ──────────────
  // convert.lsp per-slot: wdt wdq wdd wde wdw wdh wda wdp wdx wdo wdi
  // VLX mode: so prefix, 9 cols/row, 24 rows (different structure)
  standardOpenings: {
    varKey:   'wdvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.wdvar,
    columns: {
      // Source-mode per-slot positions
      3:  { field: 'x',          type: 'number' },   // wdd — distance from ref edge
      4:  { field: 'y',          type: 'number' },   // wde — elevation
      5:  { field: 'width',      type: 'number' },   // wdw
      6:  { field: 'height',     type: 'number' },   // wdh
      7:  { field: 'sillHeight', type: 'number' },   // wda — sill height (tentative)
      // 1: wdt — toggle (handled by adapter enable logic)
      // 2: wdq — side L/R (no cv-web field)
      // 8: wdp — panel ref, 9: wdx, 10: wdo, 11: wdi — unmapped
    },
  },

  // ── blockoutOpenings (rovar) — 9 cols/slot, 4 slots ──────────────────────
  // convert.lsp per-slot: rot roq rod row roh roa rop rox ros
  // VLX mode: bo prefix, 12 cols/row, 30 rows (different structure)
  blockoutOpenings: {
    varKey:   'rovar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.rovar,
    columns: {
      3:  { field: 'x',      type: 'number' },       // rod — distance from ref edge
      4:  { field: 'width',  type: 'number' },       // row
      5:  { field: 'height', type: 'number' },       // roh
      // 1: rot — toggle, 2: roq — side
      // 6: roa — arch height, 7: rop — panel, 8: rox, 9: ros — unmapped
      // y: no confident source-mode mapping (rox col 8 is tentative)
    },
  },

  // ── manDoors (drvar) — 8 cols/slot, 4 slots ─────────────────────────────
  // convert.lsp per-slot: drt drq drd dre drw drh drs drx
  // VLX mode: dr prefix, 9 cols/row, 6 rows
  // VLX confirmed: dr_1_1="22"→mark, dr_1_3=36→width, dr_1_7=92→height
  manDoors: {
    varKey:   'drvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.drvar,
    columns: {
      3:  { field: 'x',      type: 'number' },       // drd — distance from ref edge
      4:  { field: 'y',      type: 'number' },       // dre — elevation/threshold
      5:  { field: 'width',  type: 'number' },       // drw
      6:  { field: 'height', type: 'number' },       // drh
      // 1: drt — toggle, 2: drq — side
      // 7: drs — sill/swing (default "R"), 8: drx — jamb (tentative)
    },
  },

  // ── dockLevelers (dlvar) — 9 cols/slot, 3 slots ─────────────────────────
  // convert.lsp per-slot: dlt dlq dld dle dlw dlh dlx dly dlp
  dockLevelers: {
    varKey:   'dlvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.dlvar,
    columns: {
      3:  { field: 'x',      type: 'number' },       // dld — distance
      4:  { field: 'y',      type: 'number' },       // dle — elevation
      5:  { field: 'width',  type: 'number' },       // dlw
      6:  { field: 'height', type: 'number' },       // dlh
      7:  { field: 'depth',  type: 'number' },       // dlx — pit depth (default 8')
      // 1: dlt — toggle, 2: dlq — side, 8: dly, 9: dlp — unmapped
    },
  },

  // ── pilasters (plvar) — 6 cols/slot, 3 slots ────────────────────────────
  // convert.lsp per-slot: plt plq pld plw plh plx
  pilasters: {
    varKey:   'plvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.plvar,
    columns: {
      4:  { field: 'width',  type: 'number' },       // plw
      // 1: plt — toggle, 2: plq — side (L/R → maps to PlData.side)
      // 3: pld — position, 5: plh — projection?, 6: plx — height?
      // Uncertain: plh/plx field assignment needs empirical validation
    },
  },

  // ── lintels (llvar) — 7 cols/slot, 3 slots ──────────────────────────────
  // convert.lsp per-slot: llt llq lld lle llw llh llx
  lintels: {
    varKey:   'llvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.llvar,
    columns: {
      // Tentative — need empirical validation:
      // 3: lld → overOpening distance, 4: lle → depth,
      // 5: llw → height, 6: llh → projection
      // All uncertain, leaving unmapped until validated
    },
  },

  // ── chamfers (chvar) — 13 source-mode cols ──────────────────────────────
  // convert.lsp: chto cht1 chd1 chs1 cht2 chd2 chs2 cht3 chd3 chs3 cht4 chd4 chs4
  // Pattern per edge: (toggle, direction, size) × 4 edges (top/bottom/left/right)
  // VLX mode: ch prefix, only 4 cols (different representation)
  chamfers: {
    varKey:  'chvar',
    kind:    'object',
    columns: {
      // Source-mode: chs# is the chamfer SIZE for each edge
      4:  { field: 'top',    type: 'number' },       // chs1 — top edge size
      7:  { field: 'bottom', type: 'number' },       // chs2 — bottom edge size
      10: { field: 'left',   type: 'number' },       // chs3 — left edge size
      13: { field: 'right',  type: 'number' },       // chs4 — right edge size
      // 1: chto — overall toggle/reveal
      // 2,5,8,11: cht# — per-edge toggle (0/1)
      // 3,6,9,12: chd# — per-edge direction ("both")
    },
  },

  // ── footingSteps (fsvar) — 4 cols/slot, 2 slots ─────────────────────────
  // convert.lsp per-slot: fst fss fsh fsw
  footingSteps: {
    varKey:   'fsvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.fsvar,
    columns: {
      3:  { field: 'height', type: 'number' },       // fsh — step height
      4:  { field: 'width',  type: 'number' },       // fsw — step width
      // 1: fst — toggle, 2: fss — side L/R
    },
  },

  // ── slabDowels (sdvar) — 5 cols/slot, 3 slots ───────────────────────────
  // convert.lsp per-slot: sdt sde sda sdo sdi
  // Defaults: "0" "-3\"" "2'" "1'-6\"" "6\""
  slabDowels: {
    varKey:   'sdvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.sdvar,
    columns: {
      2:  { field: 'x',        type: 'number' },     // sde — edge offset position
      3:  { field: 'spacing',  type: 'number' },     // sda — dowel spacing
      4:  { field: 'embedLen', type: 'number' },     // sdo — embedment length
      5:  { field: 'projLen',  type: 'number' },     // sdi — projection length
      // 1: sdt — toggle
    },
  },

  // ── weldConnections (wcvar) — 11 cols/slot, 18 slots ────────────────────
  // convert.lsp per-slot: wct wcs wcq wcp wci wcd wce wcw wch wcy wcx
  // Note: wcvar slots start at index 0 (wct0) in convert.lsp
  // VLX mode: wc prefix, 20 cols/row, 48 rows (much larger structure)
  weldConnections: {
    varKey:   'wcvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.wcvar,
    columns: {
      6:  { field: 'x',      type: 'number' },       // wcd — distance/X position
      7:  { field: 'y',      type: 'number' },       // wce — elevation/Y position
      8:  { field: 'width',  type: 'number' },       // wcw — plate width
      9:  { field: 'height', type: 'number' },       // wch — plate height
      // 1: wct — toggle, 2: wcs — style, 3: wcq — side
      // 4: wcp — position, 5: wci — id, 10: wcy, 11: wcx — unmapped
    },
  },

  // ── pickPoints (ppvar) — complex header+slot structure ───────────────────
  // convert.lsp: ppa ppc ppr ppt1 ppq1 ppd1 ppt2 ppq2 ppd2 ... ppt5 ppe5 ...
  // Headers (3): ppa, ppc, ppr
  // Slots 1-4: per-slot t,q,d (3 fields)
  // Slots 5-8: per-slot t,e (2 fields)
  // VLX mode: pp prefix, 13 cols/row, 8 rows (different structure)
  pickPoints: {
    varKey:   'ppvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.ppvar,
    columns: {
      // Source-mode: complex non-uniform slot structure — needs special handling
      // Leaving unmapped until empirically validated
    },
  },

  // ── bracePoints (bpvar) — header+slot structure ──────────────────────────
  // convert.lsp: bpa bpn bpt1 bpq1 bpd1 bpe1 ... (2 headers + 4 per-slot)
  // VLX mode: bp prefix, 12 cols/row, 6 rows (different structure)
  bracePoints: {
    varKey:   'bpvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.bpvar,
    columns: {
      // Source-mode: 2 header vars then 4-field slots — needs special handling
      // Leaving unmapped until empirically validated
    },
  },

  // ── revisions (rvvar) — cv-cad only, not yet in cv-web ────────────────────
  revisions: {
    varKey:  'rvvar' as string,
    kind:    'array',
    maxSlots: 0,
    columns: {},
  },

  // ── horizontalFeatureStrips (fhvar) — 15 cols/slot, 18 slots ────────────
  // convert.lsp per-slot: fht fhq fhd fhl fhr fhw fha fhy fhx fhz fhp fho fhs fhi fhc
  horizontalFeatureStrips: {
    varKey:   'fhvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.fhvar,
    columns: {
      3:  { field: 'y',      type: 'number' },       // fhd — Y position from bottom
      4:  { field: 'xStart', type: 'number' },       // fhl — left extent
      5:  { field: 'xEnd',   type: 'number' },       // fhr — right extent
      6:  { field: 'width',  type: 'number' },       // fhw
      7:  { field: 'height', type: 'number' },       // fha — strip height (tentative)
      8:  { field: 'depth',  type: 'number' },       // fhy — groove depth (tentative)
      // 1: fht — toggle, 2: fhq — side
      // 9-15: fhx fhz fhp fho fhs fhi fhc — style/options, unmapped
    },
  },

  // ── verticalFeatureStrips (fvvar) — 12 cols/slot, 18 slots ──────────────
  // convert.lsp per-slot: fvt fvq fvd fve fvh fvy fvx fvz fvp fvo fvi fvc
  // NOTE: convert.lsp has BUG — fvvar slots 2-18 use "fh" prefix instead of "fv"
  verticalFeatureStrips: {
    varKey:   'fvvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.fvvar,
    columns: {
      3:  { field: 'x',     type: 'number' },        // fvd — X position
      4:  { field: 'width', type: 'number' },        // fve — strip width (tentative)
      6:  { field: 'depth', type: 'number' },        // fvy — groove depth (tentative)
      // 1: fvt — toggle, 2: fvq — side
      // 5: fvh, 7: fvx, 8-12 — unmapped/uncertain
      // yStart/yEnd mapping uncertain without empirical data
    },
  },

  // ── ledgerBars (lbvar) — 12 cols/slot, 4 slots ──────────────────────────
  // convert.lsp per-slot: lbt lbd lbl lbr lbw lbx lbs lby lbh lba lbo lbi
  // cv-web LbVar is an object (not array), so only first slot is relevant
  ledgerBars: {
    varKey:  'lbvar',
    kind:    'object',
    columns: {
      // Source-mode object section — first slot only
      1:  { field: 'enabled', type: 'boolean' },     // lbt — toggle
      // Remaining 11 fields need empirical validation
      // lbd, lbl, lbr, lbw, lbx, lbs, lby, lbh, lba, lbo, lbi
    },
  },

  // ── topPlates (tpvar) — 7 cols ──────────────────────────────────────────
  // convert.lsp: tpt1 tpx1 tps1 tpy1 tpa1 tpo1 tpi1
  // Defaults: "0" "4 X 6" "down" "3/4 X 7 1/2" "2'" "0\"" "6\""
  topPlates: {
    varKey:  'tpvar',
    kind:    'object',
    columns: {
      1:  { field: 'enabled', type: 'boolean' },     // tpt1 — toggle
      // 2: tpx1 — size spec "4 X 6" (not simple number)
      // Fields width/thickness/material need empirical validation
    },
  },

  // ── topSteps (tsvar) — 4 cols/slot, 2 slots ─────────────────────────────
  // convert.lsp per-slot: tst tss tse tsw
  topSteps: {
    varKey:   'tsvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.tsvar,
    columns: {
      4:  { field: 'width',  type: 'number' },       // tsw — step width
      // 1: tst — toggle, 2: tss — side L/R
      // 3: tse — step x/edge position (tentative → "x" field)
    },
  },

  // ── spandrelSeats (ssvar) — 5 cols/slot, 2 slots ────────────────────────
  // convert.lsp per-slot: sst sss sse ssw ssh
  spandrelSeats: {
    varKey:   'ssvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.ssvar,
    columns: {
      4:  { field: 'width',  type: 'number' },       // ssw
      5:  { field: 'height', type: 'number' },       // ssh
      // 1: sst — toggle, 2: sss — side L/R, 3: sse — x position
    },
  },

  // ── squareBlockouts (sbvar) — 8 cols/slot, 6 slots ──────────────────────
  // convert.lsp per-slot: sbt sbs sbq sbd sbe sbw sbh sbi
  squareBlockouts: {
    varKey:   'sbvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.sbvar,
    columns: {
      4:  { field: 'x',      type: 'number' },       // sbd — distance/X
      5:  { field: 'y',      type: 'number' },       // sbe — elevation/Y
      6:  { field: 'width',  type: 'number' },       // sbw
      7:  { field: 'height', type: 'number' },       // sbh
      // 1: sbt — toggle, 2: sbs — style, 3: sbq — side
      // 8: sbi — depth/inset (tentative → "depth" field)
    },
  },

  // ── roundBlockouts (rbvar) — 5 cols/slot, 6 slots ───────────────────────
  // convert.lsp per-slot: rbt rbq rbd rbe rbw
  roundBlockouts: {
    varKey:   'rbvar',
    kind:     'array',
    maxSlots: CV_WEB_SLOT_COUNTS.rbvar,
    columns: {
      3:  { field: 'x',        type: 'number' },     // rbd — distance/X
      4:  { field: 'y',        type: 'number' },     // rbe — elevation/Y
      5:  { field: 'diameter', type: 'number' },     // rbw — width (=diameter for round)
      // 1: rbt — toggle, 2: rbq — side
    },
  },
};
